import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

// Import pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CalendarPage from './pages/CalendarPage';
import ReservationsPage from './pages/ReservationsPage';
import GuestsPage from './pages/GuestsPage';
import SalesPage from './pages/SalesPage';
import ProductsPage from './pages/ProductsPage';
import ExpensesPage from './pages/ExpensesPage';
import CashPage from './pages/CashPage';
import EmployeesPage from './pages/EmployeesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import PayoutsPage from './pages/PayoutsPage';
import ChargesPage from './pages/ChargesPage';
import SuppliersPage from './pages/SuppliersPage';
import PaxListPage from './pages/PaxListPage';
import PaymentsPage from './pages/PaymentsPage';
import PreCheckinPage from './pages/PreCheckinPage';
import SetupWizardPage from './pages/SetupWizardPage';
import SuperAdminPage from './pages/SuperAdminPage';
import AccountPage from './pages/AccountPage';
import Sidebar from './components/Sidebar';
import Breadcrumb from './components/Breadcrumb';
import { ModulesProvider, useModules } from './hooks/useModules';

// Admin pages
import {
  AdminLayout,
  AdminDashboard,
  CustomersPage,
  CustomerDetailPage,
  ConciergesPage,
  ConciergeDetailPage,
  BackupsPage,
  RiadsPage,
  RiadDetailPage,
  SubscriptionsPage,
  AccessPage,
  AuditPage,
  BillingPage,
  BillingDashboard,
  NotificationsPage,
  PlansPage,
  ConfigSaaSPage,
  PlatformUsersPage,
  PlatformSettingsPage
} from './pages/admin';

import LandingPage from './pages/LandingPage';

import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Permission checking helper
export const hasPermission = (permissions, required) => {
  if (!permissions || !Array.isArray(permissions)) return false;
  if (permissions.includes('*')) return true;
  if (permissions.includes(required)) return true;
  
  // Check for wildcard patterns (e.g., 'reservations.*')
  const parts = required.split('.');
  if (parts.length > 1) {
    const wildcardPermission = parts[0] + '.*';
    if (permissions.includes(wildcardPermission)) return true;
  }
  
  return false;
};

// Check any of multiple permissions
export const hasAnyPermission = (permissions, requiredList) => {
  return requiredList.some(p => hasPermission(permissions, p));
};

// Axios instance
export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token AND riad ID to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add X-Riad-ID header for tenant isolation
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      // Check both possible field names
      const riadId = user.current_riad_id || user.riad_id || user.current_riad?.id;
      if (riadId) {
        config.headers['X-Riad-ID'] = riadId;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  return config;
});

// Handle auth errors and server errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (error.response?.status >= 500) {
      // Show error toast for server errors
      const message = error.response?.data?.error || 'Erreur serveur. Veuillez réessayer.';
      // Import dynamically to avoid circular dependency
      import('sonner').then(({ toast }) => {
        toast.error(message);
      });
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch fresh profile from API
  const refreshProfile = async () => {
    try {
      const response = await api.get('/auth/me');
      const profileData = response.data;
      localStorage.setItem('user', JSON.stringify(profileData));
      setUser(profileData);
      return profileData;
    } catch (error) {
      console.error('Failed to refresh profile:', error);
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      if (storedUser && token) {
        // Set stored user immediately for fast UI
        setUser(JSON.parse(storedUser));
        // Then refresh from API to get latest data (current_riad, accesses, permissions)
        await refreshProfile();
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user: loginUser } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(loginUser));
      setUser(loginUser);
      
      // Refresh profile to get full data including accesses and current_riad
      await refreshProfile();
      
      // Dispatch event to trigger modules refresh
      window.dispatchEvent(new Event('riad-switched'));
      
      toast.success('Connexion réussie');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur de connexion');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Déconnexion réussie');
  };

  const can = useCallback((permission) => {
    return hasPermission(user?.permissions, permission);
  }, [user?.permissions]);

  const canAny = useCallback((permissionList) => {
    return hasAnyPermission(user?.permissions, permissionList);
  }, [user?.permissions]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, can, canAny, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, requiredPermissions, requiredModule, requireSuperAdmin, skipSetupCheck }) => {
  const { user, loading, canAny } = useAuth();
  const { modules, loading: modulesLoading } = useModules();
  const location = useLocation();

  if (loading || modulesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if setup is needed (redirect to wizard if not completed)
  // Skip this check for the setup page itself and super admin pages
  if (!skipSetupCheck && !requireSuperAdmin && user.current_riad_setup_state && user.current_riad_setup_state !== 'COMPLETED') {
    return <Navigate to="/setup" replace />;
  }

  // Check super admin access
  if (requireSuperAdmin && !user.is_super_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check module access
  if (requiredModule && modules[requiredModule] === false) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check permissions if required
  if (requiredPermissions && requiredPermissions.length > 0) {
    if (!canAny(requiredPermissions)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="app-layout">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="main-content">
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100"
            data-testid="mobile-menu-btn"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </header>
        {/* Suspension Alert */}
        {user?.current_riad?.status === 'SUSPENDED' && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4" data-testid="suspension-alert">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
              <p className="text-red-700 font-medium">
                Établissement suspendu. {user?.current_riad?.suspension_reason || 'Contactez le support pour plus d\'informations.'}
              </p>
            </div>
          </div>
        )}
        {user?.subscription_status === 'SUSPENDED' && user?.current_riad?.status !== 'SUSPENDED' && (
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 m-4" data-testid="subscription-alert">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-orange-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              <p className="text-orange-700 font-medium">
                Abonnement suspendu. Veuillez régulariser votre situation.
              </p>
            </div>
          </div>
        )}
        <div className="p-4 md:p-6 lg:p-8">
          <Breadcrumb />
          {children}
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ModulesProvider>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            {/* Public Landing Page - accessible without auth at root */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            {/* Public pre-check-in page - no auth required */}
          <Route path="/checkin/:token" element={<PreCheckinPage />} />
          {/* Setup Wizard - requires auth but skips setup check */}
          <Route path="/setup" element={
            <ProtectedRoute skipSetupCheck>
              <SetupWizardPage />
            </ProtectedRoute>
          } />
          {/* Dashboard - protected, requires auth */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <AppLayout>
                <DashboardPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/calendar" element={
            <ProtectedRoute requiredPermissions={['reservations.view']} requiredModule="calendar">
              <AppLayout>
                <CalendarPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/reservations" element={
            <ProtectedRoute requiredPermissions={['reservations.view']} requiredModule="reservations">
              <AppLayout>
                <ReservationsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/guests" element={
            <ProtectedRoute requiredPermissions={['guests.view']} requiredModule="guests">
              <AppLayout>
                <GuestsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales" element={
            <ProtectedRoute requiredPermissions={['sales.view']} requiredModule="pos">
              <AppLayout>
                <SalesPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/products" element={
            <ProtectedRoute requiredPermissions={['products.view']} requiredModule="pos">
              <AppLayout>
                <ProductsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/expenses" element={
            <ProtectedRoute requiredPermissions={['expenses.view']} requiredModule="expenses">
              <AppLayout>
                <ExpensesPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/cash" element={
            <ProtectedRoute requiredPermissions={['cash.view']} requiredModule="payments">
              <AppLayout>
                <CashPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/employees" element={
            <ProtectedRoute requiredPermissions={['hr.view', 'hr.clock']} requiredModule="hr">
              <AppLayout>
                <EmployeesPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute requiredPermissions={['reports.view']} requiredModule="reports">
              <AppLayout>
                <ReportsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute requiredPermissions={['settings.view']}>
              <AppLayout>
                <SettingsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/configuration" element={
            <Navigate to="/settings?tab=config" replace />
          } />
          <Route path="/suppliers" element={
            <ProtectedRoute requiredPermissions={['suppliers.view', 'expenses.view']} requiredModule="expenses">
              <AppLayout>
                <SuppliersPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/pax" element={
            <ProtectedRoute requiredPermissions={['reservations.view']} requiredModule="guests">
              <AppLayout>
                <PaxListPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute requiredPermissions={['users.view']}>
              <AppLayout>
                <UsersPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/payouts" element={
            <ProtectedRoute requiredPermissions={['finance.view']} requiredModule="payouts">
              <AppLayout>
                <PayoutsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/payments" element={
            <ProtectedRoute requiredPermissions={['finance.view']} requiredModule="payments">
              <AppLayout>
                <PaymentsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/charges" element={
            <ProtectedRoute requiredPermissions={['expenses.view']} requiredModule="charges">
              <AppLayout>
                <ChargesPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          {/* Super Admin Routes - New Layout */}
          <Route path="/admin" element={
            <ProtectedRoute requireSuperAdmin>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
            <Route path="riads" element={<RiadsPage />} />
            <Route path="riads/:id" element={<RiadDetailPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="billing-dashboard" element={<BillingDashboard />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="plans" element={<PlansPage />} />
            <Route path="concierges" element={<ConciergesPage />} />
            <Route path="concierges/:id" element={<ConciergeDetailPage />} />
            <Route path="users" element={<PlatformUsersPage />} />
            <Route path="access" element={<AccessPage />} />
            <Route path="backups" element={<BackupsPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="config" element={<ConfigSaaSPage />} />
            <Route path="settings" element={<PlatformSettingsPage />} />
          </Route>
          
          {/* Legacy super admin route */}
          <Route path="/super-admin" element={
            <ProtectedRoute requireSuperAdmin>
              <AppLayout>
                <SuperAdminPage />
              </AppLayout>
            </ProtectedRoute>
          } />

          {/* Account Portal - SaaS client view (owner only, no tenant context) */}
          <Route path="/account" element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </ModulesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
